/**
 * SettingsStore — local-first. Reads from IndexedDB, writes there first,
 * then pushes to server in background.
 */
import { atom } from 'jotai'
import type { createStore } from 'jotai'
import { atomWithQuery } from 'jotai-tanstack-query'
import { hc } from 'hono/client'
import type { AuthApi } from '@backend/auth'
import {
  db, getUser, getWeeklyPlan, setWeeklyPlan,
  type CustomExerciseTemplate, type LocalCustomExercise, type LocalWeeklyPlan,
} from '@/lib/db'
import { pushAllPending } from '@/lib/sync'
import { invalidateLocalData } from '@/lib/invalidate'
import { apiFetch } from '@/lib/apiFetch'
import { PRESET_BY_KEY } from '@/lib/exerciseRegistry'

const authApi = hc<AuthApi>('/api/auth', { fetch: apiFetch })

export type WeekdayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun'

type Store = ReturnType<typeof createStore>

export const refreshAtom = atomWithQuery(() => ({
  queryKey: ['SettingsStore'],
  queryFn: async () => {
    const [exerciseRows, settingsRows, customRows, user] = await Promise.all([
      db.userExercises.toArray(),
      db.exerciseSettings.toArray(),
      db.customExercises.toArray(),
      getUser(),
    ])

    const exercises = exerciseRows
      .filter(e => !e.pendingDelete)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map(e => e.exerciseKey)

    const customExercises = customRows
      .filter(c => !c.pendingDelete)
      .sort((a, b) => a.sortOrder - b.sortOrder)

    const settings = settingsRows.reduce((acc, s) => {
      acc[s.exerciseKey] = {
        plankDuration: s.plankDuration,
        plankRestDuration: s.plankRestDuration,
        curlRestDuration: s.curlRestDuration,
        curlWeight: s.curlWeight,
        pushUpRestDuration: s.pushUpRestDuration,
        dumbbellPressRestDuration: s.dumbbellPressRestDuration,
        dumbbellPressWeight: s.dumbbellPressWeight,
        gluteBridgeRestDuration: s.gluteBridgeRestDuration,
        squatRestDuration: s.squatRestDuration,
      }
      return acc
    }, {} as Record<string, {
      plankDuration: number | null; plankRestDuration: number | null;
      curlRestDuration: number | null; curlWeight: number | null;
      pushUpRestDuration: number | null;
      dumbbellPressRestDuration: number | null; dumbbellPressWeight: number | null;
      gluteBridgeRestDuration: number | null; squatRestDuration: number | null;
    }>)

    return { exercises, settings, customExercises, user }
  },
}))

export const addExerciseAtom = atom(null, async (_get, _set, exerciseKey: string) => {
  const key = exerciseKey
  const existing = await db.userExercises.get(key)
  if (existing && !existing.pendingDelete) {
    // Already added — no-op
    return
  }

  // Determine sort order
  const all = await db.userExercises.toArray()
  const maxOrder = all.length > 0 ? Math.max(...all.map(e => e.sortOrder)) : -1

  await db.userExercises.put({
    exerciseKey: key,
    sortOrder: maxOrder + 1,
    createdAt: new Date().toISOString(),
    syncStatus: 'pending',
    pendingDelete: false,
  })

  await invalidateLocalData()
  void pushAllPending().catch(() => {})
})

export const removeExerciseAtom = atom(null, async (_get, _set, exerciseKey: string) => {
  const existing = await db.userExercises.get(exerciseKey)
  if (!existing) return

  if (existing.syncStatus === 'pending' && !existing.pendingDelete) {
    // Never pushed to server — just delete locally
    await db.userExercises.delete(exerciseKey)
  } else {
    // Synced; mark as pending deletion, will be removed on server push
    await db.userExercises.update(exerciseKey, {
      syncStatus: 'pending',
      pendingDelete: true,
    })
  }

  await invalidateLocalData()
  void pushAllPending().catch(() => {})
})

export const updateSettingsAtom = atom(null, async (_get, _set, params: {
  exerciseKey: string
  plankDuration?: number
  plankRestDuration?: number
  curlRestDuration?: number
  curlWeight?: number
  pushUpRestDuration?: number
  dumbbellPressRestDuration?: number
  dumbbellPressWeight?: number
  gluteBridgeRestDuration?: number
  squatRestDuration?: number
}) => {
  const existing = await db.exerciseSettings.get(params.exerciseKey)
  const next = {
    exerciseKey: params.exerciseKey,
    plankDuration: params.plankDuration ?? existing?.plankDuration ?? null,
    plankRestDuration: params.plankRestDuration ?? existing?.plankRestDuration ?? null,
    curlRestDuration: params.curlRestDuration ?? existing?.curlRestDuration ?? null,
    curlWeight: params.curlWeight ?? existing?.curlWeight ?? null,
    pushUpRestDuration: params.pushUpRestDuration ?? existing?.pushUpRestDuration ?? null,
    dumbbellPressRestDuration: params.dumbbellPressRestDuration ?? existing?.dumbbellPressRestDuration ?? null,
    dumbbellPressWeight: params.dumbbellPressWeight ?? existing?.dumbbellPressWeight ?? null,
    gluteBridgeRestDuration: params.gluteBridgeRestDuration ?? existing?.gluteBridgeRestDuration ?? null,
    squatRestDuration: params.squatRestDuration ?? existing?.squatRestDuration ?? null,
    syncStatus: 'pending' as const,
  }
  await db.exerciseSettings.put(next)

  await invalidateLocalData()
  void pushAllPending().catch(() => {})
})

// ============================================================================
// Custom exercises
// ============================================================================

export interface CreateCustomExerciseParams {
  name: string
  icon: string
  template: CustomExerciseTemplate
  exerciseType: 'aerobic' | 'anaerobic'
  restDuration: number | null
  defaultDuration?: number | null  // countdown
  weight?: number | null           // sets_with_rest
  // Quick-rep presets — only used by manual_reps template
  quickReps1?: number | null
  quickReps2?: number | null
  quickReps3?: number | null
}

/** Create a new custom exercise and auto-add it to the home page. */
export const createCustomExerciseAtom = atom(null, async (_get, _set, params: CreateCustomExerciseParams) => {
  const exerciseKey = `custom_${crypto.randomUUID()}`

  // sortOrder: append after both built-in user exercises and existing customs
  const [allUserEx, allCustoms] = await Promise.all([
    db.userExercises.toArray(),
    db.customExercises.toArray(),
  ])
  const maxUserOrder = allUserEx.length > 0 ? Math.max(...allUserEx.map(e => e.sortOrder)) : -1
  const maxCustomOrder = allCustoms.length > 0 ? Math.max(...allCustoms.map(c => c.sortOrder)) : -1
  const sortOrder = Math.max(maxUserOrder, maxCustomOrder) + 1

  const now = new Date().toISOString()
  const row: LocalCustomExercise = {
    exerciseKey,
    name: params.name.trim(),
    icon: params.icon,
    template: params.template,
    exerciseType: params.exerciseType,
    restDuration: params.restDuration,
    defaultDuration: params.defaultDuration ?? null,
    weight: params.weight ?? null,
    quickReps1: params.quickReps1 ?? null,
    quickReps2: params.quickReps2 ?? null,
    quickReps3: params.quickReps3 ?? null,
    sortOrder,
    createdAt: now,
    syncStatus: 'pending',
    pendingDelete: false,
  }

  // Write custom-exercise metadata AND the user_exercises entry so it shows on Home
  await db.transaction('rw', [db.customExercises, db.userExercises], async () => {
    await db.customExercises.put(row)
    await db.userExercises.put({
      exerciseKey,
      sortOrder,
      createdAt: now,
      syncStatus: 'pending',
      pendingDelete: false,
    })
  })

  await invalidateLocalData()
  void pushAllPending().catch(() => {})
  return exerciseKey
})

/** Add a preset exercise to the home page. Idempotent — restores a soft-deleted
 * row if one exists for the same preset key. */
export const addPresetExerciseAtom = atom(null, async (_get, _set, presetKey: string) => {
  const preset = PRESET_BY_KEY[presetKey]
  if (!preset) throw new Error(`Unknown preset: ${presetKey}`)

  // sortOrder: append to end of current user_exercises list
  const allUserEx = await db.userExercises.toArray()
  const maxOrder = allUserEx.length > 0 ? Math.max(...allUserEx.map(e => e.sortOrder)) : -1
  const sortOrder = maxOrder + 1
  const now = new Date().toISOString()

  const presetQuickReps = preset.quickReps
  await db.transaction('rw', [db.customExercises, db.userExercises], async () => {
    const existingCustom = await db.customExercises.get(preset.exerciseKey)
    if (existingCustom) {
      // Restore (clear pendingDelete) and refresh fields in case preset config changed.
      // Preserve user's customized quickReps / weight / defaultDuration if they exist.
      await db.customExercises.update(preset.exerciseKey, {
        name: preset.name,
        icon: preset.icon,
        template: preset.template,
        exerciseType: preset.exerciseType,
        restDuration: preset.restDuration,
        defaultDuration: existingCustom.defaultDuration ?? preset.defaultDuration ?? null,
        weight: existingCustom.weight ?? preset.weight ?? null,
        quickReps1: existingCustom.quickReps1 ?? presetQuickReps?.[0] ?? null,
        quickReps2: existingCustom.quickReps2 ?? presetQuickReps?.[1] ?? null,
        quickReps3: existingCustom.quickReps3 ?? presetQuickReps?.[2] ?? null,
        sortOrder,
        pendingDelete: false,
        syncStatus: 'pending',
      })
    } else {
      const row: LocalCustomExercise = {
        exerciseKey: preset.exerciseKey,
        name: preset.name,
        icon: preset.icon,
        template: preset.template,
        exerciseType: preset.exerciseType,
        restDuration: preset.restDuration,
        defaultDuration: preset.defaultDuration ?? null,
        weight: preset.weight ?? null,
        quickReps1: presetQuickReps?.[0] ?? null,
        quickReps2: presetQuickReps?.[1] ?? null,
        quickReps3: presetQuickReps?.[2] ?? null,
        sortOrder,
        createdAt: now,
        syncStatus: 'pending',
        pendingDelete: false,
      }
      await db.customExercises.put(row)
    }

    const existingUser = await db.userExercises.get(preset.exerciseKey)
    if (existingUser) {
      await db.userExercises.update(preset.exerciseKey, {
        sortOrder,
        pendingDelete: false,
        syncStatus: 'pending',
      })
    } else {
      await db.userExercises.put({
        exerciseKey: preset.exerciseKey,
        sortOrder,
        createdAt: now,
        syncStatus: 'pending',
        pendingDelete: false,
      })
    }
  })

  await invalidateLocalData()
  void pushAllPending().catch(() => {})
})

/** Update a custom-or-preset exercise's mutable fields. Marks row pending so
 * the next sync pushes it. Only fields present in `patch` are changed. */
export const updateCustomExerciseAtom = atom(null, async (_get, _set, params: {
  exerciseKey: string
  patch: Partial<Pick<LocalCustomExercise, 'name' | 'icon' | 'restDuration' | 'defaultDuration' | 'weight' | 'quickReps1' | 'quickReps2' | 'quickReps3'>>
}) => {
  const existing = await db.customExercises.get(params.exerciseKey)
  if (!existing) throw new Error(`Custom exercise not found: ${params.exerciseKey}`)

  await db.customExercises.update(params.exerciseKey, {
    ...params.patch,
    syncStatus: 'pending',
  })

  await invalidateLocalData()
  void pushAllPending().catch(() => {})
})

/** Delete a custom exercise. Workout history is preserved (rows are only removed from
 * customExercises + userExercises; workoutSessions stay untouched). */
export const removeCustomExerciseAtom = atom(null, async (_get, _set, exerciseKey: string) => {
  await db.transaction('rw', [db.customExercises, db.userExercises], async () => {
    const customRow = await db.customExercises.get(exerciseKey)
    const userRow = await db.userExercises.get(exerciseKey)

    if (customRow) {
      if (customRow.syncStatus === 'pending' && !customRow.pendingDelete) {
        await db.customExercises.delete(exerciseKey)
      } else {
        await db.customExercises.update(exerciseKey, { syncStatus: 'pending', pendingDelete: true })
      }
    }
    if (userRow) {
      if (userRow.syncStatus === 'pending' && !userRow.pendingDelete) {
        await db.userExercises.delete(exerciseKey)
      } else {
        await db.userExercises.update(exerciseKey, { syncStatus: 'pending', pendingDelete: true })
      }
    }
  })

  await invalidateLocalData()
  void pushAllPending().catch(() => {})
})

// ============================================================================
// Weekly plan
// ============================================================================

export const weeklyPlanAtom = atomWithQuery(() => ({
  queryKey: ['SettingsStore', 'weeklyPlan'],
  queryFn: async (): Promise<LocalWeeklyPlan> => {
    return await getWeeklyPlan()
  },
}))

export const updateWeeklyPlanDayAtom = atom(null, async (_get, _set, params: { day: WeekdayKey; text: string | null }) => {
  const current = await getWeeklyPlan()
  const next: LocalWeeklyPlan = {
    ...current,
    [params.day]: params.text,
    updatedAt: new Date().toISOString(),
    syncStatus: 'pending',
  }
  await setWeeklyPlan(next)
  await invalidateLocalData()
  void pushAllPending().catch(() => {})
})

// ============================================================================
// MCP API token (one per user, login required)
// ============================================================================

export const apiTokenAtom = atomWithQuery(() => ({
  queryKey: ['SettingsStore', 'apiToken'],
  queryFn: async (): Promise<{ token: string | null }> => {
    const res = await authApi['api-token'].get.$get()
    if (!res.ok) return { token: null }
    return await res.json()
  },
  staleTime: Infinity,
}))

export const createApiTokenAtom = atom(null, async (_get, _set): Promise<string> => {
  const res = await authApi['api-token'].create.$post()
  if (!res.ok) throw new Error('生成 token 失败')
  const { token } = await res.json()
  return token
})

export const revokeApiTokenAtom = atom(null, async (_get, _set): Promise<void> => {
  const res = await authApi['api-token'].revoke.$post()
  if (!res.ok) throw new Error('撤销 token 失败')
})

export function loader(store: Store) {
  store.get(refreshAtom)
  return null
}
